import {observer} from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';

import {scrollHighlightedIntoView} from 'core/utils.js';
import NameSearchResult, {
    profileHref,
} from 'directory/components/NameSearchResult.js';
import {MIN_QUERY_LENGTH} from 'directory/stores/NameSearchStore.js';

export const TOO_SHORT = `Please type at least ${MIN_QUERY_LENGTH} characters`;
export const NO_RESULTS = 'No results';

/**
 * The empty state the tabbed widgets show. The standalone name-search widget
 * shows something else entirely - an invitation to create a profile - so it
 * passes its own.
 *
 * Not named `renderNoResults`: SpecialtySearchPanel exports a function by
 * that name which returns a whole `<li>`, where this returns the row's
 * contents. Importing the wrong one nests an `<li>` inside an `<li>`, which
 * stops `li.name-search-results.no-results` matching and loses the styling
 * without erroring.
 */
export function defaultNoResults() {
    return NO_RESULTS;
}

/**
 * Search-by-name, the second tab of the health fund widget.
 *
 * Replaces the `#name` block of insurer_widget.html. Two deliberate changes
 * from the Knockout version:
 *
 *  - Enter opens the highlighted result in the fund's target window. The old
 *    version hard-coded window.open(url, '_blank') for the keyboard path while
 *    the click path honoured target, so for the four funds that need _top the
 *    two routes disagreed.
 *  - the name is rendered as text with the matched run wrapped in a span,
 *    rather than through an `html:` binding.
 *
 * Deliberately no focus-on-tab-change. insurer-widget.js has a `tabShown`
 * listener that focuses the panel's input, but it is bound to
 * `a[data-toggle="tab"]` and this widget's tab anchors carry no such
 * attribute, so the listener never attaches and the original never focuses.
 * Moving focus here would also break the ARIA tab pattern, where focus stays
 * on the tab so the arrow keys keep working.
 */
function NameSearchPanel({
    // False for the standalone name-search widget, which has no tab strip:
    // `role="tabpanel"` with an `aria-labelledby` pointing at a tab that does
    // not exist is worse for a screen reader than no ARIA at all.
    asTabPanel = true,
    healthFundId = null,
    isActive = true,
    renderNoResults: renderNoResultsProp = defaultNoResults,
    skin = null,
    store,
    target = '_blank',
}) {
    const listRef = React.useRef(null);

    // The Knockout managers scrolled the list whenever the selected index
    // changed, so arrowing past the visible rows followed the highlight down.
    // Without it the highlight walks off the bottom and, because macOS only
    // shows a scrollbar while something scrolls, the list looks unscrollable.
    React.useEffect(() => {
        const list = listRef.current;
        // `.selected` is what NameSearchResult puts on the highlighted row,
        // and what style.less shades - so it cannot vanish unnoticed.
        scrollHighlightedIntoView(list, list?.querySelector('li.selected'));
    }, [store.highlightedIndex]);

    function open(url) {
        const href = profileHref(url, {healthFundId, skin});
        window.open(href, target);
    }

    function handleKeyDown(event) {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            store.moveHighlight(1);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            store.moveHighlight(-1);
        } else if (event.key === 'Escape') {
            store.clear();
        } else if (event.key === 'Enter') {
            const result = store.highlightedResult;
            if (result) {
                event.preventDefault();
                open(result.url);
            }
        }
    }

    const {loading, query, results} = store;
    const listHidden = !query.length || loading;
    const panelProps = asTabPanel
        ? {
              'aria-labelledby': 'name-tab',
              hidden: !isActive,
              id: 'name-panel',
              role: 'tabpanel',
          }
        : {};

    return (
        <div className="hs-search" {...panelProps}>
            {/*
              * `active` here is what makes the field visible at all:
              * style.less carries `#name.hs-field {display: none}` and only
              * `#name.hs-field.active` shows it. The Knockout version had no
              * panel wrapper for this tab, so manageTabs() put the class
              * straight onto `#name`. The same rule set also gates the
              * dropdown, via `.hs-field.active .autocomplete-results-holder`.
              */}
            <div
                className={isActive ? 'hs-field active' : 'hs-field'}
                id="name"
            >
                <label className="hs-label" htmlFor="name-search">
                    {'Enter name'}
                    <span className="required-field">{'*'}</span>
                </label>
                <input
                    autoComplete="off"
                    className={results.length ? 'active' : null}
                    id="name-search"
                    onChange={(event) => store.setQuery(event.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="eg. 'Smith'"
                    type="text"
                    value={query}
                />
                <div className="autocomplete-results-holder">
                    <ul
                        className={listHidden ? 'results hide' : 'results'}
                        ref={listRef}
                    >
                        {results.map((result, index) => (
                            <NameSearchResult
                                healthFundId={healthFundId}
                                isHighlighted={index === store.highlightedIndex}
                                key={result.id}
                                query={query}
                                result={result}
                                skin={skin}
                                target={target}
                            />
                        ))}
                        {store.showTooShort && (
                            <li className="name-search-results no-results">
                                {TOO_SHORT}
                            </li>
                        )}
                        {store.showNoResults && (
                            <li className="name-search-results no-results">
                                {renderNoResultsProp()}
                            </li>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
}

NameSearchPanel.propTypes = {
    asTabPanel: PropTypes.bool,
    healthFundId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isActive: PropTypes.bool,
    // Returns the contents of the "nothing matched" row. The row itself, and
    // the classes style.less targets, stay here.
    renderNoResults: PropTypes.func,
    skin: PropTypes.string,
    store: PropTypes.object.isRequired,
    target: PropTypes.string,
};


export default observer(NameSearchPanel);
